/**
 * Projects Management Page
 */

import React, { useEffect, useState } from 'react';
import { Card, Table, Button, Modal, TextInput, Textarea, Badge } from 'flowbite-react';
import { HiPlus, HiPencil, HiArchive, HiSearch, HiFolderOpen } from 'react-icons/hi';
import { useStatus } from '../contexts/StatusContext';
import { projectsApi } from '../api/projects';
import type { Project } from '../types';

interface ProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: { name: string; description: string }) => Promise<void>;
  project?: Project | null;
}

const ProjectModal: React.FC<ProjectModalProps> = ({ isOpen, onClose, onSave, project }) => {
  const [name, setName] = useState(project?.project_name || '');
  const [description, setDescription] = useState(project?.description || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (project) {
      setName(project.project_name);
      setDescription(project.description || '');
    } else {
      setName('');
      setDescription('');
    }
  }, [project]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      await onSave({ name, description });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save project');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={isOpen} onClose={onClose}>
      <Modal.Header>{project ? 'Edit Project' : 'Create Project'}</Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 text-sm text-red-700 bg-red-100 rounded-md">{error}</div>
          )}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Project Name
            </label>
            <TextInput
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My Awesome Project"
              required
            />
          </div>
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of the project..."
              rows={3}
            />
          </div>
          <div className="flex justify-end space-x-2 pt-4">
            <Button color="gray" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : project ? 'Save Changes' : 'Create Project'}
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

const Projects: React.FC = () => {
  const { user, canCreateProject, usageStats } = useStatus();
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  const companyId = user?.company_id || '';

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    setIsLoading(true);
    try {
      const response = await projectsApi.list();
      if (response.success && response.data) {
        setProjects(response.data);
      } else {
        setError(response.error || 'Failed to load projects');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load projects');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveProject = async (data: { name: string; description: string }) => {
    if (selectedProject) {
      // Update existing project
      const response = await projectsApi.update(selectedProject.project_id, {
        name: data.name,
        description: data.description,
      });
      if (!response.success) {
        throw new Error(response.error || 'Failed to update project');
      }
    } else {
      // Create new project
      const response = await projectsApi.create({
        name: data.name,
        description: data.description,
        companyId,
      });
      if (!response.success) {
        throw new Error(response.error || 'Failed to create project');
      }
    }

    await loadProjects();
    setSelectedProject(null);
  };

  const handleArchiveProject = async (projectId: string) => {
    if (!confirm('Are you sure you want to archive this project?')) {
      return;
    }

    try {
      const response = await projectsApi.archive(projectId);
      if (response.success) {
        await loadProjects();
      } else {
        setError(response.error || 'Failed to archive project');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to archive project');
    }
  };

  const openCreateModal = () => {
    setSelectedProject(null);
    setIsModalOpen(true);
  };

  const openEditModal = (project: Project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const filteredProjects = projects.filter(
    (p) =>
      p.project_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const activeProjects = filteredProjects.filter((p) => !p.archived && p.status !== 'archived');
  const archivedProjects = filteredProjects.filter((p) => p.archived || p.status === 'archived');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Projects</h1>
          <p className="mt-1 text-sm text-gray-500">
            {usageStats?.projects || 0}{usageStats?.maxProjects !== null ? ` of ${usageStats?.maxProjects} projects used` : ' projects'}
          </p>
        </div>
        <Button onClick={openCreateModal} disabled={!canCreateProject()}>
          <HiPlus className="mr-2 h-4 w-4" />
          New Project
        </Button>
      </div>

      {/* Upgrade prompt if at limit */}
      {!canCreateProject() && (
        <Card className="bg-amber-50 border-amber-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-amber-800">Project limit reached</h3>
              <p className="text-sm text-amber-700">
                Upgrade your plan to create more projects.
              </p>
            </div>
            <a
              href="/subscriptions"
              className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700"
            >
              Upgrade
            </a>
          </div>
        </Card>
      )}

      {/* Error display */}
      {error && (
        <div className="p-4 text-red-700 bg-red-100 rounded-md">{error}</div>
      )}

      {/* Search */}
      <Card>
        <div className="mb-4">
          <TextInput
            icon={HiSearch}
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Projects Table */}
        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-mindmeld-primary"></div>
          </div>
        ) : activeProjects.length === 0 ? (
          <div className="text-center py-12">
            <HiFolderOpen className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No projects</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating a new project.
            </p>
            <div className="mt-6">
              <Button onClick={openCreateModal} disabled={!canCreateProject()}>
                <HiPlus className="mr-2 h-4 w-4" />
                New Project
              </Button>
            </div>
          </div>
        ) : (
          <Table>
            <Table.Head>
              <Table.HeadCell>Project</Table.HeadCell>
              <Table.HeadCell>Invariants</Table.HeadCell>
              <Table.HeadCell>Sessions</Table.HeadCell>
              <Table.HeadCell>Created</Table.HeadCell>
              <Table.HeadCell>Updated</Table.HeadCell>
              <Table.HeadCell>Actions</Table.HeadCell>
            </Table.Head>
            <Table.Body className="divide-y">
              {activeProjects.map((project) => (
                <Table.Row key={project.project_id} className="bg-white">
                  <Table.Cell>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{project.project_name}</p>
                      {project.description && (
                        <p className="text-sm text-gray-500 truncate max-w-xs">
                          {project.description}
                        </p>
                      )}
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge color="purple">{project.invariant_count || 0}</Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge color="blue">{project.session_count || 0}</Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-sm text-gray-500">
                      {new Date(project.created_at).toLocaleDateString()}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-sm text-gray-500">
                      {project.updated_at ? new Date(project.updated_at).toLocaleDateString() : '-'}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex space-x-2">
                      <button
                        className="p-1 text-gray-400 hover:text-gray-600"
                        title="Edit project"
                        onClick={() => openEditModal(project)}
                      >
                        <HiPencil className="h-4 w-4" />
                      </button>
                      <button
                        className="p-1 text-gray-400 hover:text-red-600"
                        title="Archive project"
                        onClick={() => handleArchiveProject(project.project_id)}
                      >
                        <HiArchive className="h-4 w-4" />
                      </button>
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}

        {/* Archived Projects */}
        {archivedProjects.length > 0 && (
          <div className="mt-8">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Archived Projects</h3>
            <Table>
              <Table.Head>
                <Table.HeadCell>Project</Table.HeadCell>
                <Table.HeadCell>Archived</Table.HeadCell>
              </Table.Head>
              <Table.Body className="divide-y">
                {archivedProjects.map((project) => (
                  <Table.Row key={project.project_id} className="bg-gray-50">
                    <Table.Cell>
                      <p className="text-sm text-gray-500">{project.project_name}</p>
                    </Table.Cell>
                    <Table.Cell>
                      <span className="text-sm text-gray-400">
                        {project.updated_at ? new Date(project.updated_at).toLocaleDateString() : '-'}
                      </span>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}
      </Card>

      {/* Project Modal */}
      <ProjectModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedProject(null);
        }}
        onSave={handleSaveProject}
        project={selectedProject}
      />
    </div>
  );
};

export default Projects;
